/**
 * Окно уходимости ОДНОЙ КНОПКОЙ-ДРОПДАУНОМ.
 *
 * Пришло на смену трём чипам 15.09.26 (замечание владельца разметкой:
 * «уходимость сделай тоже выпадающим списком … добавь также 90 дней»; спека —
 * `docs/specs/2026-09-15-card-sales-fold-window-picker-design.md`). Вид — тот
 * же, что у соседнего `PeriodPicker`: серая плашка названия, значение, шеврон.
 *
 * Своего состояния окна здесь НЕТ: значение живёт в адресе (`w`) и разбирается
 * `speedWindow()`, общим с шестерёнкой листания. Компонент только показывает и
 * сообщает выбор.
 */

import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";

import { PeriodPicker, Строка } from "@/components/PeriodPicker";
import { iso, lastDays } from "@/lib/dates";
import { SPEED_WINDOWS, type WindowValue, speedWindowLabel, windowLength, windowOf, windowSpan } from "@/lib/reorder";
import { usePopover } from "@/lib/usePopover";

/** Подсказки пунктов. У окна без записи — общая фраза. */
const HINTS: Partial<Record<number, string>> = {
  7: "за неделю у трёх четвертей размеров движения нет вовсе — окно для ходовых",
  60: "окно по умолчанию",
  90:
    "витрина Ozon ведётся с 27.07.2026 — Ozon-часть окна короче 90 дней, " +
    "и у товара, который продаётся на Ozon, уходимость занижена",
};

export function WindowPicker({
  days,
  onChange,
}: {
  days: number;
  onChange: (days: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Клик мимо и Esc закрывают список — тем же хуком, что у `PeriodPicker`.
  usePopover(open, () => setOpen(false), boxRef);

  return (
    <div ref={boxRef} className="relative" data-annot="окно уходимости">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`чистый выкуп двух площадок за последние ${days} дней`}
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (open ? "border-border-strong" : "border-border hover:border-border-strong")
        }
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
          уходимость
        </span>
        <span className="truncate px-2 tabular-nums text-fg">{days} дней</span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
      </button>

      {open ? (
        // Попап: Enter на его кнопке нажимает кнопку, а не применяет тулбар «Товаров».
        <div
          className="absolute top-[34px] left-0 z-30 w-[180px] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg"
          data-popup=""
        >
          <div className="py-1">
            {SPEED_WINDOWS.map((option) => (
              <Строка
                key={option}
                label={`${option} дней`}
                title={HINTS[option] ?? `чистый выкуп за последние ${option} дней`}
                on={option === days}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Витрина Ozon ведётся с этого дня: у периода раньше Ozon-часть пуста. */
const OZON_FROM = "2026-07-27";

/**
 * Окно уходимости раздела «Товары» — ТЕМ ЖЕ ПИКЕРОМ, что период (разметка
 * 26.09.26 06:36 п.2: «такой же date picker, как и период»): пресеты 7 · 30 ·
 * 60 · 90, сезоны-кварталы с годом и свой период календарём. «Последние N
 * дней» остаются пресетом `w=N`, остальное — периодом `w=A:B`.
 */
export function SpeedWindowPicker({ w, onChange }: { w: WindowValue; onChange: (next: WindowValue) => void }) {
  const [from, to] = windowSpan(w) ?? lastDays(Number(w));
  const раньшеOzon = from < OZON_FROM;
  return (
    <PeriodPicker
      label="уходимость"
      annot="окно уходимости"
      from={from}
      to={to}
      since={null}
      days={windowLength(w)}
      max={iso(new Date())}
      presets={SPEED_WINDOWS.map((days) => ({
        days,
        label: `${days} дней`,
        hint: HINTS[days] ?? `чистый выкуп за последние ${days} дней`,
      }))}
      hint={
        `чистый выкуп двух площадок за ${speedWindowLabel(w)}` +
        (раньшеOzon ? ` — витрина Ozon ведётся с 27.07.2026, раньше считается только WB` : "")
      }
      onChange={(nextFrom, nextTo) => onChange(windowOf(nextFrom, nextTo, iso(new Date())))}
    />
  );
}
